import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  Send,
  Sparkles,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Loader2,
  ArrowRight,
  AlertCircle,
  ExternalLink,
  Mic,
  MicOff,
  Volume2,
} from "lucide-react";
import { isSpeechRecognitionAvailable, isSpeechSynthesisAvailable, speakText, startSpeechRecognition, type RecognitionHandle } from "@/lib/speech";
import { Link } from "wouter";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ChapterModule } from "@/data/chapters/types";
import { CHAPTERS } from "@/data/chapters";
import {
  buildSingleChapterContext,
  buildLibraryContext,
  type ChatSource,
} from "@/lib/chat-context";
import { refsUsedIn, sendChat, type ChatTurn, type WebSource } from "@/lib/chat";

const MAX_QUESTION_LENGTH = 1200;
const HISTORY_TURNS_KEPT = 6;

interface Props {
  scope: "chapter" | "library";
  chapter?: ChapterModule;
  /** Externally control the open state. If omitted, the panel manages its own. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the floating Ask AI button (when a header trigger is rendered instead). */
  hideFab?: boolean;
}

interface AssistantMessage extends ChatTurn {
  role: "assistant";
  usedSources?: ChatSource[];
  webSources?: WebSource[];
  suggestions?: string[];
}

type Message = ChatTurn | AssistantMessage;

const STARTERS_CHAPTER = [
  "Summarise the key idea of this chapter.",
  "Quiz me on the most testable terms here.",
  "What's the most common misconception?",
];

const STARTERS_LIBRARY = [
  "Explain the difference between DRO and DRA.",
  "Which chapters cover reinforcement schedules?",
  "Give me a 5-question mock exam on extinction.",
];

export function AskAIHeaderButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-2.5 min-h-[40px] rounded-md bg-primary text-primary-foreground hover-elevate text-[12.5px] font-medium"
      data-testid="ask-ai-header"
      aria-label="Open the AI study assistant"
    >
      <Sparkles className="w-4 h-4" />
      <span className="hidden sm:inline">Ask AI</span>
    </button>
  );
}

export function ChatPanel({ scope, chapter, open: openProp, onOpenChange, hideFab }: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    if (openProp === undefined) setInternalOpen(next);
  };
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cap, setCap] = useState<{ count: number; daily: number; remaining: number } | null>(null);
  const [listening, setListening] = useState(false);
  const recRef = useRef<RecognitionHandle | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const toggleMic = () => {
    if (listening && recRef.current) {
      recRef.current.stop();
      recRef.current = null;
      setListening(false);
      return;
    }
    const handle = startSpeechRecognition(text => {
      setInput(prev => (prev ? `${prev} ${text}` : text).slice(0, MAX_QUESTION_LENGTH));
      setListening(false);
      recRef.current = null;
    });
    if (!handle) return;
    recRef.current = handle;
    setListening(true);
  };

  const scopeLabel = useMemo(() => {
    if (scope === "chapter" && chapter) return `Chapter ${chapter.meta.id} - ${chapter.meta.title}`;
    return "All 31 chapters";
  }, [scope, chapter]);

  const starters = scope === "chapter" ? STARTERS_CHAPTER : STARTERS_LIBRARY;

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => {
      document.body.style.overflow = prev;
      window.clearTimeout(t);
    };
  }, [open]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, busy]);

  const submit = useCallback(
    async (raw: string) => {
      const question = raw.trim();
      if (!question || busy) return;
      const next: Message[] = [
        ...messages,
        { role: "user", content: question },
        { role: "assistant", content: "", pending: true } as AssistantMessage,
      ];
      setMessages(next);
      setInput("");
      setBusy(true);
      setError(null);
      const history = messages
        .filter(m => !m.pending && !m.error)
        .slice(-HISTORY_TURNS_KEPT * 2)
        .map(m => ({ role: m.role, content: m.content }));
      const payload = scope === "chapter" && chapter
        ? buildSingleChapterContext(chapter, question, history)
        : buildLibraryContext(CHAPTERS, question, history);
      try {
        const data = await sendChat(payload);
        if (data.cap) setCap(data.cap);
        const used = refsUsedIn(data.answer, data.sources);
        setMessages(curr => {
          const copy = [...curr];
          const lastIdx = copy.length - 1;
          copy[lastIdx] = {
            role: "assistant",
            content: data.answer,
            sources: data.sources,
            usedSources: used,
            webSources: data.webSources,
            suggestions: data.suggestions,
          } as AssistantMessage;
          return copy;
        });
      } catch (e: any) {
        const code = e?.body?.error ?? e?.message ?? "network_error";
        const detail =
          code === "daily_cap_reached"
            ? `Daily cap reached (${e.body.count}/${e.body.cap}). Try again tomorrow.`
            : code === "chat_not_configured"
            ? "Chat backend is not configured yet."
            : code === "upstream_error"
            ? "The chat service is unreachable. Try again in a moment."
            : code === "blocked_by_filter"
            ? "Your question was blocked by the safety filter. Rephrase and try again."
            : "Something went wrong. Try again.";
        setError(detail);
        setMessages(curr => {
          const copy = [...curr];
          const lastIdx = copy.length - 1;
          copy[lastIdx] = {
            role: "assistant",
            content: "",
            error: detail,
          };
          return copy;
        });
      } finally {
        setBusy(false);
        inputRef.current?.focus();
      }
    },
    [busy, messages, scope, chapter],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  };

  const reset = () => {
    setMessages([]);
    setError(null);
    setInput("");
  };

  return (
    <>
      {!hideFab && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed z-30 bottom-4 right-4 sm:bottom-6 sm:right-6 inline-flex items-center gap-2 px-4 py-3 min-h-[52px] rounded-full bg-primary text-primary-foreground shadow-lg border border-primary/40 text-[13px] font-medium hover-elevate"
          data-testid="chat-fab"
          aria-label="Ask the AI study assistant"
        >
          <Sparkles className="w-4 h-4" /> Ask AI
        </button>
      )}

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[70] flex items-end sm:items-stretch sm:justify-end"
          data-testid="chat-panel"
        >
          <div className="absolute inset-0 bg-background/85 sm:bg-background/40" onClick={() => setOpen(false)} />
          <div
            className="relative w-full sm:w-[480px] sm:max-w-[92vw] bg-card sm:rounded-none rounded-t-xl border-t sm:border-t-0 sm:border-l border-border shadow-2xl flex flex-col h-[92dvh] sm:h-dvh max-h-[100dvh] animate-in slide-in-from-bottom sm:slide-in-from-right duration-150"
            onMouseDown={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-3 sm:px-4 py-3 border-b border-border">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">Ask AI</div>
                <div className="text-[13px] font-medium truncate">{scopeLabel}</div>
              </div>
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={reset}
                  className="text-[11px] uppercase tracking-wider px-2 py-1 rounded border border-border text-muted-foreground hover-elevate"
                  data-testid="chat-reset"
                >
                  Reset
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-2.5 rounded-md border border-card-border hover-elevate min-w-[40px] min-h-[40px] flex items-center justify-center"
                aria-label="Close chat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 space-y-4">
              {messages.length === 0 ? (
                <EmptyState starters={starters} onPick={s => submit(s)} disabled={busy} />
              ) : (
                messages.map((m, i) => {
                  const isLastAssistant =
                    m.role === "assistant" &&
                    !m.pending &&
                    !m.error &&
                    !messages.slice(i + 1).some(x => x.role === "assistant");
                  return (
                    <MessageBubble
                      key={i}
                      message={m}
                      busy={busy && i === messages.length - 1}
                      onSuggest={isLastAssistant && !busy ? submit : undefined}
                    />
                  );
                })
              )}
            </div>

            {error && (
              <div className="px-3 sm:px-4 py-2 border-t border-border bg-red-500/5 text-[12px] text-red-700 dark:text-red-300 flex items-center gap-2">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
              </div>
            )}

            <div className="border-t border-border px-3 sm:px-4 py-3">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value.slice(0, MAX_QUESTION_LENGTH))}
                  onKeyDown={handleKeyDown}
                  placeholder={scope === "chapter" ? "Ask about this chapter..." : "Ask about any ABA topic..."}
                  className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2.5 text-[14px] sm:text-[13.5px] min-h-[48px] max-h-[160px] focus:outline-none focus:ring-2 focus:ring-primary/40"
                  rows={1}
                  disabled={busy}
                  data-testid="chat-input"
                />
                {isSpeechRecognitionAvailable() && (
                  <button
                    type="button"
                    onClick={toggleMic}
                    disabled={busy}
                    className={`p-2.5 min-h-[48px] min-w-[48px] rounded-md border ${listening ? "border-red-500/50 bg-red-500/10 text-red-700 dark:text-red-300" : "border-border hover-elevate"}`}
                    aria-label={listening ? "Stop recording" : "Start voice input"}
                    data-testid="chat-mic"
                  >
                    {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => submit(input)}
                  disabled={busy || input.trim().length === 0}
                  className="px-4 py-3 min-h-[48px] rounded-md bg-primary text-primary-foreground disabled:opacity-40 text-[13px] font-medium inline-flex items-center gap-2"
                  data-testid="chat-send"
                  aria-label="Send"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex items-center justify-between mt-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                <span>{input.length} / {MAX_QUESTION_LENGTH}</span>
                {cap && (
                  <span data-testid="chat-cap">
                    {cap.remaining} / {cap.daily} left today
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function EmptyState({ starters, onPick, disabled }: { starters: string[]; onPick: (s: string) => void; disabled: boolean }) {
  return (
    <div className="py-6">
      <div className="flex items-center gap-2 mb-3 text-muted-foreground">
        <MessageSquare className="w-4 h-4" />
        <span className="text-[11px] uppercase tracking-[0.18em]">Try one of these</span>
      </div>
      <div className="space-y-2">
        {starters.map(s => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            disabled={disabled}
            className="w-full text-left px-3 py-3 rounded-md border border-border bg-background hover-elevate text-[13.5px] sm:text-[13px] min-h-[48px] disabled:opacity-50"
          >
            {s}
          </button>
        ))}
      </div>
      <p className="mt-6 text-[11.5px] text-muted-foreground leading-relaxed">
        Answers are grounded in the chapter content already loaded into the app. The model is told to refuse questions it cannot ground.
      </p>
    </div>
  );
}

function MessageBubble({
  message,
  busy,
  onSuggest,
}: {
  message: Message;
  busy: boolean;
  onSuggest?: (text: string) => void;
}) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[88%] sm:max-w-[80%] rounded-2xl rounded-br-md bg-primary text-primary-foreground px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap">
          {message.content}
        </div>
      </div>
    );
  }

  if (message.error) {
    return (
      <div className="rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2.5 text-[12.5px] text-red-700 dark:text-red-300 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>{message.error}</span>
      </div>
    );
  }

  if (message.pending) {
    return (
      <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Thinking...
      </div>
    );
  }

  const assistant = message as AssistantMessage;
  const speak = isSpeechSynthesisAvailable()
    ? () => speakText(assistant.content.replace(/\[(SRC|PDF)-\d+\]/g, ""))
    : null;
  return (
    <div className="space-y-2">
      <div className="rounded-2xl rounded-bl-md bg-muted/40 px-3.5 py-3 text-[13.5px] leading-relaxed">
        <MarkdownAnswer content={assistant.content} />
        {speak && (
          <button
            type="button"
            onClick={speak}
            className="mt-2 inline-flex items-center gap-1.5 text-eyebrow text-muted-foreground hover-elevate rounded px-2 py-1"
            aria-label="Read answer aloud"
            data-testid="chat-speak"
          >
            <Volume2 className="w-3.5 h-3.5" /> Read aloud
          </button>
        )}
      </div>
      {assistant.usedSources && assistant.usedSources.length > 0 && (
        <SourceDrawer sources={assistant.usedSources} />
      )}
      {assistant.webSources && assistant.webSources.length > 0 && (
        <WebSourceDrawer sources={assistant.webSources} />
      )}
      {onSuggest && assistant.suggestions && assistant.suggestions.length > 0 && (
        <SuggestPills suggestions={assistant.suggestions} onPick={onSuggest} />
      )}
    </div>
  );
}

function SuggestPills({ suggestions, onPick }: { suggestions: string[]; onPick: (text: string) => void }) {
  return (
    <div className="pt-1">
      <div className="text-eyebrow text-muted-foreground mb-1.5">Try next</div>
      <div className="flex flex-wrap gap-1.5">
        {suggestions.map((s, i) => (
          <button
            key={`${i}-${s}`}
            type="button"
            onClick={() => onPick(s)}
            className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[40px] rounded-full border border-primary/30 bg-primary/5 text-primary text-meta font-medium hover-elevate"
            data-testid={`chat-suggest-${i}`}
          >
            <ArrowRight className="w-3.5 h-3.5" />
            <span>{s}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function MarkdownAnswer({ content }: { content: string }) {
  // The model emits inline citations as [SRC-N] for chapter sources and
  // [n] for Perplexity web citations. The markdown renderer preserves the
  // brackets as text, then a post-pass converts SRC-N into superscripts.
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0 prose-headings:my-3 prose-headings:font-semibold prose-headings:text-foreground prose-strong:text-foreground prose-code:bg-muted/60 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[12px] prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:underline-offset-2">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p>{enrichCitations(children)}</p>,
          li: ({ children }) => <li>{enrichCitations(children)}</li>,
          a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

function enrichCitations(node: React.ReactNode): React.ReactNode {
  // Replace [SRC-N] tokens in any string children with styled superscripts.
  if (typeof node === "string") return citationPass(node);
  if (Array.isArray(node)) return node.map((c, i) => <span key={i}>{enrichCitations(c)}</span>);
  return node;
}

function citationPass(text: string): React.ReactNode {
  const matches = Array.from(text.matchAll(/\[((?:SRC|PDF)-\d+)\]/g));
  if (matches.length === 0) return text;
  const out: React.ReactNode[] = [];
  let lastIdx = 0;
  matches.forEach((m, key) => {
    const startIdx = m.index ?? 0;
    if (startIdx > lastIdx) out.push(text.slice(lastIdx, startIdx));
    const id = m[1];
    const isPdf = id.startsWith("PDF-");
    const n = Number(id.replace(/^(SRC|PDF)-/, ""));
    out.push(
      <sup
        key={`c-${key}`}
        className={`inline-flex items-center justify-center min-w-[18px] h-4 px-1 mx-0.5 rounded-sm text-[10px] font-medium font-mono tabular-nums no-underline ${
          isPdf
            ? "bg-amber-500/20 text-amber-700 dark:text-amber-300"
            : "bg-primary/20 text-primary"
        }`}
        title={isPdf ? `PDF page ${n}` : `Chapter source ${n}`}
      >
        {isPdf ? `P${n}` : n}
      </sup>,
    );
    lastIdx = startIdx + m[0].length;
  });
  if (lastIdx < text.length) out.push(text.slice(lastIdx));
  return out;
}

function WebSourceDrawer({ sources }: { sources: WebSource[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-border bg-background/40">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground"
        aria-expanded={open}
        data-testid="chat-web-sources-toggle"
      >
        <span className="inline-flex items-center gap-1.5">
          <ExternalLink className="w-3 h-3" /> Web sources ({sources.length})
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <ul className="border-t border-border divide-y divide-border">
          {sources.map(s => {
            let host: string | undefined;
            try {
              host = new URL(s.url).host.replace(/^www\./, "");
            } catch {
              host = undefined;
            }
            return (
              <li key={`${s.n}-${s.url}`}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-2.5 px-3 py-2.5 hover-elevate"
                  data-testid={`chat-web-source-${s.n}`}
                >
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 mt-0.5 px-1 rounded bg-secondary text-secondary-foreground text-[10px] font-medium font-mono tabular-nums">
                    {s.n}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[13px] font-medium leading-snug">{s.title || s.url}</span>
                    {host && (
                      <span className="block text-[11px] text-muted-foreground mt-0.5">{host}{s.date ? ` - ${s.date.slice(0, 10)}` : ""}</span>
                    )}
                    {s.snippet && (
                      <span className="block text-[12px] text-muted-foreground line-clamp-2 mt-0.5">{s.snippet}</span>
                    )}
                  </span>
                  <ExternalLink className="w-3.5 h-3.5 mt-1 shrink-0 text-muted-foreground" />
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function SourceDrawer({ sources }: { sources: ChatSource[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-border bg-background/40">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground"
        aria-expanded={open}
        data-testid="chat-sources-toggle"
      >
        <span>{sources.length} source{sources.length === 1 ? "" : "s"}</span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <ul className="border-t border-border divide-y divide-border">
          {sources.map(s => {
            const n = Number(s.id.replace("SRC-", ""));
            const linkBase = `/chapter/${s.chapterId}`;
            const onClick = () => {
              if (s.anchor) sessionStorage.setItem("__jumpTo", s.anchor);
            };
            return (
              <li key={s.id}>
                <Link
                  href={linkBase}
                  onClick={onClick}
                  className="flex items-start gap-2.5 px-3 py-2.5 hover-elevate"
                  data-testid={`chat-source-${s.id}`}
                >
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 mt-0.5 px-1 rounded bg-primary/15 text-primary text-[10px] font-medium font-mono tabular-nums">
                    {n}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[11.5px] uppercase tracking-wider text-muted-foreground">
                      Ch {s.chapterId} - {s.kind}{s.page ? ` - p. ${s.page}` : ""}
                    </span>
                    <span className="block text-[13px] font-medium leading-snug">{s.label}</span>
                    <span className="block text-[12px] text-muted-foreground line-clamp-2 mt-0.5">{s.text}</span>
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 mt-1 shrink-0 text-muted-foreground" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
